import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Timeline as TimelineIcon,
  Build as BuildIcon,
  Edit as PlanIcon,
  Visibility as ViewIcon,
  Flag as FlagIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material'
import { OtwAgGridReact as AgGridReact } from '@/components/AgGrid/OtwAgGridReact'
import {
  CellDoubleClickedEvent,
  ColDef,
  ICellRendererParams,
  RowDragEndEvent,
  ValueFormatterParams,
} from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { canonicalVehicleTier } from '@otw/models/inventory'
import { getRepairTruckWorkStatus, getTruckStatus } from '@otw/models/logistics'
import { isActiveRepairTruckStatus } from '@otw/models/repair'
import type { RepairTruck } from '@otw/models/logistics'
import type { TransportTruckWithVehicle } from '@/hooks/logistics/useLogistics'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { MobileRecordCard } from 'components/MobileRecordCard'
import { MobileRecordList } from 'components/MobileRecordList'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import InspectionChecklistAnswersDialog, {
  resolveCompleteChecklistId,
  resolveIntakeChecklistId,
} from '../dialogs/InspectionChecklistAnswersDialog'
import { TimelineDialog } from '../dialogs'
import { useInspectionChecklist } from '@/hooks/repair/useInspectionChecklist'
import { useRepairShopLocations } from '@/hooks/inventory/useRepairShopLocations'
import { useVehicleTimelineLatestNoteMsMap } from '@/hooks/timeline'
import { getLastRepairGridUpdateMs } from '@/utils/repairLastUpdate'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import CopyGridRowButton from '../CopyGridRowButton'
import {
  formatRepairV2RowForClipboard,
  formatDaysAgoForClipboard,
  copyTextToClipboard,
} from '../../utils/gridRowClipboard'

function priorityRankLabel(rank: number | null | undefined): string {
  return typeof rank === 'number' && Number.isFinite(rank) ? `#${rank}` : '—'
}

function vehicleYearLabel(year: number | null | undefined): string | null {
  return year != null && Number.isFinite(year) ? String(year) : null
}

function priorityDragText(
  vehicle: { year?: number | null; tier?: unknown } | null | undefined,
  rank: number | null | undefined,
): string {
  return [priorityRankLabel(rank), vehicleYearLabel(vehicle?.year), canonicalVehicleTier(vehicle?.tier)]
    .filter((part): part is string => Boolean(part))
    .join(' · ')
}

function formatDaysAgo(ms: number): string {
  const elapsedMs = Math.max(0, Date.now() - ms)
  const days = Math.floor(elapsedMs / (24 * 60 * 60 * 1000))
  return `${days} day${days === 1 ? '' : 's'} ago`
}

function formatGridDate(date?: string | null): string {
  if (!date) return '—'
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function dateToSortMs(date?: string | null): number | null {
  if (!date) return null
  const ms = new Date(date).getTime()
  return Number.isNaN(ms) ? null : ms
}

function formatDurationMs(durationMs: number): string {
  const safeMs = Math.max(0, durationMs)
  const days = Math.floor(safeMs / (24 * 60 * 60 * 1000))
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'}`
  const hours = Math.floor(safeMs / (60 * 60 * 1000))
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'}`
  const minutes = Math.floor(safeMs / (60 * 1000))
  return minutes < 1 ? '< 1 min' : `${minutes} min`
}

function formatCompletedOnDate(repairTruck: RepairTruck): string {
  const completeEntry = [...(repairTruck.work_status_history ?? [])]
    .reverse()
    .find((entry) => entry.status === 'complete')
  const completedMs = completeEntry?.timestamp?.toMillis?.()
  if (!completedMs) return '—'
  return new Date(completedMs).toLocaleDateString('en-US')
}

function formatTimeInRepair(row: TransportTruckWithVehicle, repairTruck: RepairTruck | null): string {
  if (!repairTruck) return '—'

  const stintStartMs = repairTruck.created_at?.toMillis?.()
  if (!stintStartMs) return '—'

  const isActiveStint = isActiveRepairTruckStatus(getTruckStatus(row.truck))
  if (isActiveStint) {
    return formatDurationMs(Date.now() - stintStartMs)
  }

  const completeEntry = (repairTruck.work_status_history ?? []).find(
    (entry) => entry.status === 'complete'
  )
  const completedMs = completeEntry?.timestamp?.toMillis?.()
  const latestWorkStatusMs = repairTruck.work_status_history?.length
    ? repairTruck.work_status_history[repairTruck.work_status_history.length - 1]?.timestamp?.toMillis?.()
    : undefined
  const latestWorkNoteMs = repairTruck.work_notes?.length
    ? repairTruck.work_notes[repairTruck.work_notes.length - 1]?.timestamp?.toMillis?.()
    : undefined
  const stintEndMs = completedMs ?? latestWorkStatusMs ?? latestWorkNoteMs ?? stintStartMs

  return formatDurationMs(stintEndMs - stintStartMs)
}

function getCellDisplayTextForCopy(event: CellDoubleClickedEvent<RepairV2GridRow>): string {
  const { value, colDef, data, node, api, column, context } = event
  if (!colDef) return ''

  if (typeof colDef.valueFormatter === 'function') {
    const formatted = colDef.valueFormatter({
      value,
      data,
      node,
      colDef,
      column,
      api,
      context,
    } as ValueFormatterParams<RepairV2GridRow>)
    if (formatted != null && String(formatted).trim() !== '') {
      return String(formatted)
    }
  }

  if (value == null) return ''
  return String(value)
}

const NON_COPYABLE_COLUMN_HEADERS = new Set(['Actions'])

export interface RepairV2TrucksGridProps {
  rowData: RepairV2GridRow[]
  getRepairShopLabel: (row: RepairV2GridRow) => string
  getCustomerLabel: (row: RepairV2GridRow) => string
  getInventoryStatusDisplay?: (row: RepairV2GridRow) => {
    label: string
    chipColor: 'default' | 'info' | 'error' | 'warning'
  }
  onOpenTimeline?: (row: RepairV2GridRow) => void
  onOpenTransportTruck?: (row: RepairV2GridRow) => void
  onOpenInventoryForm?: (inventoryVehicleId: string) => void
  onToggleFlag?: (repairTruck: RepairTruck, flagged: boolean) => void
  isTogglingFlag?: boolean
  onViewTruck?: (row: RepairV2GridRow) => void
  getPriorityRank?: (row: RepairV2GridRow) => number | null
  enablePriorityReorder?: boolean
  onReorderPriority?: (orderedRows: RepairV2GridRow[]) => void
}

export interface RepairV2GridRow {
  truckWithVehicle: TransportTruckWithVehicle
  repairTruck: RepairTruck
}

interface RepairActionsMenuProps {
  item: RepairV2GridRow
  onOpenTransportTruck?: (row: RepairV2GridRow) => void
  onOpenInventoryForm?: (inventoryVehicleId: string) => void
  onToggleFlag?: (repairTruck: RepairTruck, flagged: boolean) => void
  onOpenChecklist?: (repairTruck: RepairTruck) => void
  onOpenRepairTimeline?: (row: RepairV2GridRow) => void
  onOpenVehicleTimeline?: (row: RepairV2GridRow) => void
  isTogglingFlag: boolean
  checklistSummaryLabel: string
}

const RepairActionsMenu: React.FC<RepairActionsMenuProps> = ({
  item,
  onOpenTransportTruck,
  onOpenInventoryForm,
  onToggleFlag,
  onOpenChecklist,
  onOpenRepairTimeline,
  onOpenVehicleTimeline,
  isTogglingFlag,
  checklistSummaryLabel,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = Boolean(anchorEl)
  const repairTruck = item.repairTruck
  const isFlagged = Boolean(repairTruck?.flagged)

  const closeMenu = () => setAnchorEl(null)

  return (
    <>
      <Tooltip title="Actions">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={isOpen} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            onOpenTransportTruck?.(item)
            closeMenu()
          }}
        >
          <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View transport card</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={!item.truckWithVehicle.vehicle?.id}
          onClick={() => {
            const inventoryVehicleId = item.truckWithVehicle.vehicle?.id
            if (!inventoryVehicleId) return
            onOpenInventoryForm?.(inventoryVehicleId)
            closeMenu()
          }}
        >
          <ListItemIcon><PlanIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Open inventory form</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onOpenChecklist?.(repairTruck)
            closeMenu()
          }}
        >
          <ListItemIcon><AssignmentIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{checklistSummaryLabel}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onOpenRepairTimeline?.(item)
            closeMenu()
          }}
        >
          <ListItemIcon><BuildIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View repair timeline</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onOpenVehicleTimeline?.(item)
            closeMenu()
          }}
          disabled={!item.truckWithVehicle.vehicle}
        >
          <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View vehicle timeline</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (onToggleFlag && !isTogglingFlag) {
              onToggleFlag(repairTruck, !isFlagged)
            }
            closeMenu()
          }}
          disabled={isTogglingFlag}
        >
          <ListItemIcon><FlagIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{isFlagged ? 'Unflag truck' : 'Flag truck'}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

const RepairV2TrucksGrid: React.FC<RepairV2TrucksGridProps> = ({
  rowData,
  getRepairShopLabel,
  getCustomerLabel,
  getInventoryStatusDisplay,
  onOpenTimeline,
  onOpenTransportTruck,
  onOpenInventoryForm,
  onToggleFlag,
  isTogglingFlag = false,
  onViewTruck,
  getPriorityRank,
  enablePriorityReorder = false,
  onReorderPriority,
}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const { showSuccess, showError } = useSnackbar()
  const { repairShopLocations } = useRepairShopLocations()
  const repairShopLocationsById = useMemo(() => {
    const map = new Map<string, NonNullable<typeof repairShopLocations>[number]>()
    ;(repairShopLocations ?? []).forEach((shop) => {
      if (shop.id) map.set(shop.id, shop)
    })
    return map
  }, [repairShopLocations])
  const [flagConfirmState, setFlagConfirmState] = useState<{
    repairTruck: RepairTruck
    nextFlagged: boolean
  } | null>(null)
  const [selectedChecklistRepairTruck, setSelectedChecklistRepairTruck] = useState<RepairTruck | null>(null)
  const [isVehicleTimelineOpen, setIsVehicleTimelineOpen] = useState(false)
  const [selectedTimelineVehicle, setSelectedTimelineVehicle] =
    useState<TransportTruckWithVehicle['vehicle'] | null>(null)
  const {
    questions: checklistQuestions,
  } = useInspectionChecklist({ kind: 'complete', fallbackToDefault: true })

  const selectedChecklistShop = selectedChecklistRepairTruck
    ? repairShopLocationsById.get(selectedChecklistRepairTruck.repair_shop_location_id)
    : undefined
  const selectedCompleteChecklistId = resolveCompleteChecklistId(
    selectedChecklistShop
      ? {
          id: selectedChecklistRepairTruck?.repair_shop_location_id,
          inspection_checklist_id: selectedChecklistShop.inspection_checklist_id,
        }
      : selectedChecklistRepairTruck
        ? { id: selectedChecklistRepairTruck.repair_shop_location_id }
        : null,
  )
  const selectedIntakeChecklistId = resolveIntakeChecklistId(
    selectedChecklistShop
      ? {
          id: selectedChecklistRepairTruck?.repair_shop_location_id,
          intake_checklist_id: selectedChecklistShop.intake_checklist_id,
        }
      : selectedChecklistRepairTruck
        ? { id: selectedChecklistRepairTruck.repair_shop_location_id }
        : null,
  )

  const vehicleIdsForTimelineLastUpdate = useMemo(
    () => rowData.map((r) => r.truckWithVehicle.vehicle?.id),
    [rowData],
  )
  const timelineNoteLatestMsMap = useVehicleTimelineLatestNoteMsMap(vehicleIdsForTimelineLastUpdate)

  const getVehicleTimelineNoteMs = useCallback(
    (vehicleId: string | undefined) => (vehicleId ? timelineNoteLatestMsMap.get(vehicleId) : undefined),
    [timelineNoteLatestMsMap],
  )

  const columnDefs = useMemo<ColDef<RepairV2GridRow>[]>(
    () => [
      ...(onOpenTimeline
        ? [
            {
              headerName: 'Actions',
              cellRenderer: (params: ICellRendererParams<RepairV2GridRow>) => {
                const item = params.data
                if (!item) return null
                const repairTruck = item.repairTruck
                const isFlagged = Boolean(repairTruck?.flagged)
                const enabledChecklistQuestions = checklistQuestions.filter((question) => question.enabled !== false)
                const totalChecklistQuestions = enabledChecklistQuestions.length
                const answeredChecklistQuestions = enabledChecklistQuestions.filter((question) => {
                  const answer = repairTruck?.inspection_checklist?.responses?.[question.id]?.answer
                  return answer === 'yes' || answer === 'no' || answer === 'na'
                }).length
                const isChecklistComplete =
                  Boolean(repairTruck) &&
                  totalChecklistQuestions > 0 &&
                  answeredChecklistQuestions >= totalChecklistQuestions
                const checklistSummaryLabel = `Checklist answers (${answeredChecklistQuestions}/${totalChecklistQuestions})`

                const repairClipboardCtx = {
                  getRepairShopLabel,
                  getCustomerLabel,
                  getInventoryStatusLabel: (gridRow: RepairV2GridRow) =>
                    getInventoryStatusDisplay?.(gridRow).label ?? 'Unknown',
                  getPriorityLabel: (gridRow: RepairV2GridRow) => {
                    if (!getPriorityRank) return '—'
                    const rank = getPriorityRank(gridRow)
                    return typeof rank === 'number' && Number.isFinite(rank) ? String(rank) : '—'
                  },
                  getLastUpdateLabel: (gridRow: RepairV2GridRow) => {
                    const vehicleId = gridRow.truckWithVehicle.vehicle?.id
                    const ms = getLastRepairGridUpdateMs(
                      gridRow.repairTruck,
                      getVehicleTimelineNoteMs(vehicleId),
                    )
                    return typeof ms === 'number' ? formatDaysAgoForClipboard(ms) : '—'
                  },
                  getChecklistSummaryLabel: () => checklistSummaryLabel,
                }

                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <CopyGridRowButton
                      getCopyText={() =>
                        formatRepairV2RowForClipboard(item, repairClipboardCtx)
                      }
                    />
                    <RepairActionsMenu
                      item={item}
                      onOpenTransportTruck={onOpenTransportTruck}
                      onOpenInventoryForm={onOpenInventoryForm}
                      onToggleFlag={(truck, flagged) =>
                        setFlagConfirmState({ repairTruck: truck, nextFlagged: flagged })
                      }
                      onOpenChecklist={(truck) => setSelectedChecklistRepairTruck(truck)}
                      onOpenRepairTimeline={onOpenTimeline}
                      onOpenVehicleTimeline={(row) => {
                        if (!row.truckWithVehicle.vehicle) return
                        setSelectedTimelineVehicle(row.truckWithVehicle.vehicle)
                        setIsVehicleTimelineOpen(true)
                      }}
                      isTogglingFlag={isTogglingFlag}
                      checklistSummaryLabel={checklistSummaryLabel}
                    />
                  </Box>
                )
              },
              sortable: false,
              filter: false,
              pinned: 'left',
              lockPinned: true,
              suppressMovable: true,
              flex: 0,
              minWidth: 96,
              maxWidth: 96,
            } as ColDef<RepairV2GridRow>,
          ]
        : []),
      {
        headerName: 'Priority',
        valueGetter: (params) => {
          if (!params.data) return '—'
          return priorityDragText(params.data.truckWithVehicle.vehicle, getPriorityRank?.(params.data))
        },
        cellRenderer: (params: ICellRendererParams<RepairV2GridRow>) => {
          const vehicle = params.data?.truckWithVehicle.vehicle
          const rank = params.data && getPriorityRank ? getPriorityRank(params.data) : null
          const year = vehicleYearLabel(vehicle?.year)
          const tier = canonicalVehicleTier(vehicle?.tier)
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%', minWidth: 0 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {priorityRankLabel(rank)}
              </Box>
              {year ? <Box component="span">{year}</Box> : null}
              {tier ? <Chip size="small" variant="outlined" label={tier} sx={{ height: 22 }} /> : null}
            </Box>
          )
        },
        rowDrag: enablePriorityReorder,
        rowDragText: (params) => {
          const row = params.rowNode?.data as RepairV2GridRow | undefined
          if (!row) return params.defaultTextValue
          return priorityDragText(row.truckWithVehicle.vehicle, getPriorityRank?.(row))
        },
        sortable: false,
        filter: false,
        flex: 0,
        minWidth: 220,
        maxWidth: 280,
      },
      {
        headerName: 'Last update',
        valueGetter: (params) => {
          if (!params.data) return null
          const repairTruck = params.data.repairTruck
          const vehicleId = params.data.truckWithVehicle.vehicle?.id
          return getLastRepairGridUpdateMs(repairTruck, getVehicleTimelineNoteMs(vehicleId))
        },
        valueFormatter: (params) =>
          typeof params.value === 'number' ? formatDaysAgo(params.value) : '—',
        comparator: (valueA, valueB) => {
          const left = typeof valueA === 'number' ? valueA : -1
          const right = typeof valueB === 'number' ? valueB : -1
          return left - right
        },
        flex: 0,
        minWidth: 140,
        maxWidth: 160,
      },
      {
        headerName: 'Vehicle',
        valueGetter: (params) => {
          const v = params.data?.truckWithVehicle.vehicle
          return v ? `${v.year ?? ''} ${v.make ?? ''} ${v.model ?? ''}`.trim() || '—' : '—'
        },
        flex: 1,
        minWidth: 140,
      },
      {
        headerName: 'Stock #',
        valueGetter: (params) => params.data?.truckWithVehicle.vehicle?.stock_number ?? '—',
        flex: 0,
        minWidth: 80,
      },
      {
        headerName: 'Tier',
        valueGetter: (params) => canonicalVehicleTier(params.data?.truckWithVehicle.vehicle?.tier) || '—',
        flex: 0,
        minWidth: 110,
      },
      {
        headerName: 'VIN',
        valueGetter: (params) =>
          params.data?.truckWithVehicle.truck?.vin ??
          params.data?.truckWithVehicle.vehicle?.vin ??
          '—',
        flex: 0,
        minWidth: 120,
      },
      {
        headerName: 'Repair shop',
        valueGetter: (params) => (params.data ? getRepairShopLabel(params.data) : '—'),
        flex: 0,
        minWidth: 160,
      },
      {
        headerName: 'Customer',
        valueGetter: (params) => (params.data ? getCustomerLabel(params.data) : '—'),
        flex: 0,
        minWidth: 180,
      },
      {
        headerName: 'Status',
        valueGetter: (params) => {
          if (!params.data || !getInventoryStatusDisplay) return 'Unknown'
          return getInventoryStatusDisplay(params.data).label
        },
        cellRenderer: (params: ICellRendererParams<RepairV2GridRow>) => {
          if (!params.data || !getInventoryStatusDisplay) return '—'
          const inventoryStatusDisplay = getInventoryStatusDisplay(params.data)
          return (
            <Chip
              size="small"
              color={inventoryStatusDisplay.chipColor}
              label={inventoryStatusDisplay.label}
            />
          )
        },
        flex: 0,
        minWidth: 280,
      },
      {
        headerName: 'Funded date',
        valueGetter: (params) =>
          dateToSortMs(params.data?.truckWithVehicle.vehicle?.funded_date),
        valueFormatter: (params) =>
          formatGridDate(params.data?.truckWithVehicle.vehicle?.funded_date),
        comparator: (valueA, valueB) => {
          const left = typeof valueA === 'number' ? valueA : -1
          const right = typeof valueB === 'number' ? valueB : -1
          return left - right
        },
        flex: 0,
        minWidth: 130,
      },
      {
        headerName: 'Paid date',
        valueGetter: (params) => {
          const vehicle = params.data?.truckWithVehicle.vehicle
          return dateToSortMs(vehicle?.paid_for_date ?? vehicle?.title_tracking?.paid_date)
        },
        valueFormatter: (params) => {
          const vehicle = params.data?.truckWithVehicle.vehicle
          return formatGridDate(vehicle?.paid_for_date ?? vehicle?.title_tracking?.paid_date)
        },
        comparator: (valueA, valueB) => {
          const left = typeof valueA === 'number' ? valueA : -1
          const right = typeof valueB === 'number' ? valueB : -1
          return left - right
        },
        flex: 0,
        minWidth: 130,
      },
      {
        headerName: 'Time in repair',
        valueGetter: (params) => {
          if (!params.data) return '—'
          return formatTimeInRepair(params.data.truckWithVehicle, params.data.repairTruck)
        },
        flex: 0,
        minWidth: 120,
      },
      {
        headerName: 'Repairs completed',
        valueGetter: (params) => {
          if (!params.data) return '—'
          return formatCompletedOnDate(params.data.repairTruck)
        },
        flex: 0,
        minWidth: 130,
      },
      {
        headerName: 'Work status',
        valueGetter: (params) => {
          if (!params.data) return '—'
          return getRepairTruckWorkStatus(params.data.repairTruck)
        },
        flex: 0,
        minWidth: 110,
      },
      {
        headerName: 'Work notes',
        valueGetter: (params) => {
          if (!params.data) return '—'
          const r = params.data.repairTruck
          const notes = r?.work_notes
          if (!notes?.length) return '—'
          return notes[notes.length - 1].note || '—'
        },
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: 'Paid',
        valueGetter: (params) => {
          if (!params.data) return '—'
          const r = params.data.repairTruck
          return r.paid ? 'Yes' : 'No'
        },
        flex: 0,
        minWidth: 70,
      },
      ...(onViewTruck
        ? [
            {
              headerName: 'Actions',
              cellRenderer: (params: ICellRendererParams<RepairV2GridRow>) => {
                const item = params.data
                if (!item) return null
                return (
                  <Tooltip title="View truck">
                    <IconButton
                      size="small"
                      onClick={() => onViewTruck(item)}
                      aria-label="View truck"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              },
              sortable: false,
              filter: false,
              flex: 0,
              minWidth: 90,
            } as ColDef<RepairV2GridRow>,
          ]
        : []),
    ],
    [
      getRepairShopLabel,
      getCustomerLabel,
      getInventoryStatusDisplay,
      onOpenTimeline,
      onToggleFlag,
      onOpenTransportTruck,
      onOpenInventoryForm,
      isTogglingFlag,
      onViewTruck,
      checklistQuestions,
      getPriorityRank,
      enablePriorityReorder,
      getVehicleTimelineNoteMs,
      timelineNoteLatestMsMap,
    ]
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      editable: false,
    }),
    []
  )

  const handleCellDoubleClicked = useCallback(
    async (event: CellDoubleClickedEvent<RepairV2GridRow>) => {
      const headerName = event.colDef?.headerName ?? ''
      if (NON_COPYABLE_COLUMN_HEADERS.has(headerName)) return

      const text = getCellDisplayTextForCopy(event).trim()
      if (!text) return

      const ok = await copyTextToClipboard(text)
      if (ok) {
        showSuccess('Cell copied to clipboard')
      } else {
        showError('Could not copy to clipboard')
      }
    },
    [showSuccess, showError],
  )

  return (
    <Box
      sx={{
        height: isMobile ? 'auto' : '70vh',
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isMobile ? (
        <Box sx={{ p: 1.5 }}>
          <MobileRecordList isEmpty={rowData.length === 0} empty="No repair trucks match the current filters.">
            {rowData.map(item => {
              const vehicle = item.truckWithVehicle.vehicle
              const priorityLabel = priorityDragText(vehicle, getPriorityRank?.(item))
              const vehicleName = [vehicle?.make, vehicle?.model].filter(Boolean).join(' ')
              const title = vehicle
                ? [priorityLabel, vehicle.stock_number || '—', vehicleName].filter(Boolean).join(' · ')
                : `${priorityLabel} · ${item.repairTruck.id}`
              const enabledChecklistQuestions = checklistQuestions.filter(question => question.enabled !== false)
              const totalChecklistQuestions = enabledChecklistQuestions.length
              const answeredChecklistQuestions = enabledChecklistQuestions.filter(question => {
                const answer = item.repairTruck?.inspection_checklist?.responses?.[question.id]?.answer
                return answer === 'yes' || answer === 'no' || answer === 'na'
              }).length
              return (
                <MobileRecordCard
                  key={item.repairTruck.id}
                  title={title}
                  meta={`${getRepairShopLabel(item)} · ${getRepairTruckWorkStatus(item.repairTruck)}`}
                  detail={[getCustomerLabel(item), formatTimeInRepair(item.truckWithVehicle, item.repairTruck)].filter(Boolean).join(' · ')}
                  trailing={
                    <RepairActionsMenu
                      item={item}
                      onOpenTransportTruck={onOpenTransportTruck}
                      onOpenInventoryForm={onOpenInventoryForm}
                      onToggleFlag={(truck, flagged) =>
                        setFlagConfirmState({ repairTruck: truck, nextFlagged: flagged })
                      }
                      onOpenChecklist={truck => setSelectedChecklistRepairTruck(truck)}
                      onOpenRepairTimeline={onOpenTimeline}
                      onOpenVehicleTimeline={row => {
                        if (!row.truckWithVehicle.vehicle) return
                        setSelectedTimelineVehicle(row.truckWithVehicle.vehicle)
                        setIsVehicleTimelineOpen(true)
                      }}
                      isTogglingFlag={isTogglingFlag}
                      checklistSummaryLabel={`Checklist answers (${answeredChecklistQuestions}/${totalChecklistQuestions})`}
                    />
                  }
                />
              )
            })}
          </MobileRecordList>
        </Box>
      ) : (
      <div
        className={theme.palette.mode === 'dark' ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'}
        style={{ height: '100%', width: '100%', flex: 1 }}
      >
        <AgGridReact<RepairV2GridRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data.repairTruck.id}
          domLayout="normal"
          animateRows
          suppressCellFocus
          rowDragManaged={enablePriorityReorder}
          onCellDoubleClicked={handleCellDoubleClicked}
          onRowDragEnd={(event: RowDragEndEvent<RepairV2GridRow>) => {
            if (!enablePriorityReorder || !onReorderPriority) return
            const orderedRows: RepairV2GridRow[] = []
            event.api.forEachNodeAfterFilterAndSort((node) => {
              if (node.data) orderedRows.push(node.data)
            })
            onReorderPriority(orderedRows)
          }}
        />
      </div>
      )}
      <ConfirmDialog
        open={!!flagConfirmState}
        title={flagConfirmState?.nextFlagged ? 'Flag this truck?' : 'Remove flag from this truck?'}
        message={
          flagConfirmState?.nextFlagged
            ? 'This will mark the repair truck for attention.'
            : 'This will clear the attention flag from the repair truck.'
        }
        confirmText={flagConfirmState?.nextFlagged ? 'Flag truck' : 'Remove flag'}
        cancelText="Cancel"
        severity="warning"
        onCancel={() => setFlagConfirmState(null)}
        onConfirm={() => {
          if (!flagConfirmState || !onToggleFlag) return
          onToggleFlag(flagConfirmState.repairTruck, flagConfirmState.nextFlagged)
          setFlagConfirmState(null)
        }}
      />
      {selectedChecklistRepairTruck && (
        <InspectionChecklistAnswersDialog
          open={!!selectedChecklistRepairTruck}
          onClose={() => setSelectedChecklistRepairTruck(null)}
          repairTruck={selectedChecklistRepairTruck}
          completeChecklistId={selectedCompleteChecklistId}
          intakeChecklistId={selectedIntakeChecklistId}
          initialTab="complete"
        />
      )}
      {selectedTimelineVehicle && (
        <TimelineDialog
          open={isVehicleTimelineOpen}
          onClose={() => {
            setIsVehicleTimelineOpen(false)
            setSelectedTimelineVehicle(null)
          }}
          vehicle={selectedTimelineVehicle}
        />
      )}
    </Box>
  )
}

export default RepairV2TrucksGrid
